/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1781049600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jun 10 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "shopify",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "GraphQL vs REST API",
          "depth": 0,
          "id": "graphql-vs-rest-api"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Shopify",
          "depth": 0,
          "id": "connect-to-shopify"
        }, {
          "value": "Create Client credentials for your custom app",
          "depth": 1,
          "id": "create-client-credentials-for-your-custom-app"
        }, {
          "value": "Create an Admin API access token for your custom app",
          "depth": 1,
          "id": "create-an-admin-api-access-token-for-your-custom-app"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Objects",
          "depth": 1,
          "id": "objects"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Actions",
          "depth": 1,
          "id": "actions"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Tag sync behavior",
          "depth": 1,
          "id": "tag-sync-behavior"
        }, {
          "value": "Variants field formatting",
          "depth": 1,
          "id": "variants-field-formatting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3",
      ol: "ol",
      li: "li",
      img: "img",
      pre: "pre",
      code: "code",
      ul: "ul",
      h4: "h4",
      span: "span"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Shopify will be ", _jsx(_components.a, {
            href: "https://shopify.dev/docs/apps/build/graphql/migrate",
            children: "deprecating their REST\nAPI"
          }), " and recommends their\nnew ", _jsx(_components.a, {
            href: "https://shopify.dev/docs/api/admin-graphql",
            children: "GraphQL API"
          }), ". Hightouch\nsupports using these new GraphQL endpoints for new syncs, and existing syncs\ncan be updated to use the GraphQL endpoints."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "This destination lets you send customer, product, and fulfillment information from your data warehouse to Shopify so you never have to worry about stale inventory information or incorrect customer or product details."
      }), "\n", _jsx(_components.h2, {
        id: "graphql-vs-rest-api",
        children: "GraphQL vs REST API"
      }), "\n", _jsxs(_components.p, {
        children: ["Shopify will be deprecating their REST API in favor of their new GraphQL API. For more detailed information on this update, and how object types and inputs have changed, refer to this ", _jsx(_components.a, {
          href: "https://shopify.dev/docs/apps/build/graphql/migrate",
          children: "Shopify resource"
        }), ". Hightouch allows syncs to use either protocol for the time being, and you can change between the two protocols to see the differences between them. Most notably, the GraphQL endpoints use new object types ", _jsx(_components.strong, {
          children: "Product Variant"
        }), " and ", _jsx(_components.strong, {
          children: "Product Option"
        }), " that were previously coupled together for the REST API. They are now separate Object types to sync to based on existing ", _jsx(_components.strong, {
          children: "Products"
        }), " in your shop."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#objects",
                children: _jsx(_components.strong, {
                  children: "Objects"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to customer, discount, inventory item, inventory level, and product objects"
            }), _jsx(_components.td, {
              children: "Varies depending on object"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://shopify.dev/docs/api/admin-graphql",
                children: "GraphQL Admin API ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#events",
                children: _jsx(_components.strong, {
                  children: "Events"
                })
              })
            }), _jsx(_components.td, {
              children: "Send fulfillment events to Shopify"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://shopify.dev/docs/api/admin-rest/2024-01/resources/fulfillmentevent#post-orders-order-id-fulfillments-fulfillment-id-events",
                children: "Create fulfillment event ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#actions",
                children: _jsx(_components.strong, {
                  children: "Actions"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to various Shopify actions"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://shopify.dev/docs/api/admin-graphql/2024-01/mutations/fulfillmentOrderCancel",
                children: "Fulfillment order actions ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-shopify",
        children: "Connect to Shopify"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Shopify"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To connect your Hightouch app to Shopify, you need to create a ", _jsx(_components.strong, {
          children: "custom app"
        }), " in your Shopify account."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Shopify requires that custom apps made after January 1, 2026 be authenticated\nto using ", _jsx(_components.strong, {
            children: "Client credentials"
          }), ". Custom apps made before January 1, 2026 can\nbe authenticated to using an ", _jsx(_components.strong, {
            children: "Admin API access token"
          }), ". If you are creating a\nnew custom app, please use the Client credentials flow. If you have an\nexisting custom app, you may use the Admin API access token flow."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You then need to provide your ", _jsx(_components.strong, {
          children: "Store name"
        }), " and either the set of ", _jsx(_components.strong, {
          children: "Client credentials"
        }), " or an ", _jsx(_components.strong, {
          children: "Admin API access token"
        }), " of your custom app."]
      }), "\n", _jsx(_components.h3, {
        id: "create-client-credentials-for-your-custom-app",
        children: "Create Client credentials for your custom app"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In your Shopify store's home page, click ", _jsx(_components.strong, {
              children: "Apps"
            }), " on the sidebar.\n", _jsx(_components.img, {
              src: "destinations/destination-shopify-home-page-app.png",
              alt: "Creating an app in the Shopify home page"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click on ", _jsx(_components.strong, {
              children: "Develop apps"
            }), " and then select ", _jsx(_components.strong, {
              children: "Build apps in Dev Dashboard"
            }), ".\n", _jsx(_components.img, {
              src: "destinations/destination-shopify-develop-apps.png",
              alt: "Develop apps button in Shopify"
            }), "\n", _jsx(_components.img, {
              src: "destinations/destination-shopify-dev-dashboard-button.png",
              alt: "Build apps in Dev Dashboard button in Shopify"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Create app"
            }), " and go through the flow using the ", _jsx(_components.strong, {
              children: "Start from Dev Dashboard"
            }), " option.\n", _jsx(_components.img, {
              src: "destinations/destination-shopify-apps-page.png",
              alt: "Shopify apps page"
            }), "\n", _jsx(_components.img, {
              src: "destinations/destination-shopify-create-an-app.png",
              alt: "Shopify create an app page"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Add to the ", _jsx(_components.strong, {
              children: "Access > Scopes"
            }), " section the scopes:"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "write_customers,\nread_customers,\nwrite_discounts,\nread_discounts,\nwrite_inventory,\nread_inventory,\nwrite_price_rules,\nread_price_rules,\nwrite_products,\nread_products,\nread_locations,\nwrite_assigned_fulfillment_orders,\nread_assigned_fulfillment_orders,\nwrite_merchant_managed_fulfillment_orders,\nread_merchant_managed_fulfillment_orders,\nwrite_fulfillments,\nread_fulfillments,\nwrite_orders,\nread_orders\n"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Release"
          }), " and under the ", _jsx(_components.strong, {
            children: "Settings"
          }), " tab you will find your ", _jsx(_components.strong, {
            children: "Client ID"
          }), " and ", _jsx(_components.strong, {
            children: "Client secret"
          }), ".\n", _jsx(_components.img, {
            src: "destinations/destination-shopify-custom-app-client-credentials.png",
            alt: "Shopify Custom app client credentials"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "create-an-admin-api-access-token-for-your-custom-app",
        children: "Create an Admin API access token for your custom app"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In your Shopify store's admin, click ", _jsx(_components.strong, {
            children: "Apps"
          }), " in the left sidebar."]
        }), "\n", _jsxs(_components.li, {
          children: ["From the dropdown which appears, select ", _jsx(_components.strong, {
            children: "Apps and sales channel settings"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-shopify-add-app.png",
          alt: "Creating an app in the Shopify admin"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Develop apps for your store"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the next screen, click ", _jsx(_components.strong, {
            children: "Create an App"
          }), " in the top right corner. Give it a descriptive name, for example, \"Hightouch integration.\" Then click ", _jsx(_components.strong, {
            children: "Create app"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab, select ", _jsx(_components.strong, {
            children: "Configure"
          }), " to configure the Admin API Integration."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-shopify-configure.png",
          alt: "Configuring an Admin API token"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "6",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the following ", _jsx(_components.strong, {
            children: "Admin API access scopes"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_customers"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_customers"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_discounts"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_discounts"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_inventory"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_inventory"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_price_rules"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_price_rules"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_products"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_products"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_locations"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_assigned_fulfillment_orders"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_assigned_fulfillment_orders"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_merchant_managed_fulfillment_orders"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_merchant_managed_fulfillment_orders"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_fulfillments"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "read_fulfillments"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "write_orders"
              })
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.code, {
                children: "read_orders"
              }), "\n", _jsx(_components.img, {
                src: "destinations/destination-shopify-api-access-scopes.png",
                alt: "Required scopes in the Shopify Admin"
              })]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), " in the bottom right of the screen."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "API Credentials"
          }), " tab, click ", _jsx(_components.strong, {
            children: "Install app"
          }), ", and then ", _jsx(_components.strong, {
            children: "Install"
          }), " in the modal that appears."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your ", _jsx(_components.strong, {
            children: "Admin API Access Token"
          }), " is now available. Copy and save your Admin API access token in a secure place."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Shopify destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Shopify destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "objects",
        children: "Objects"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to these Shopify objects with the following sync modes:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customers"
              })
            }), _jsx(_components.td, {
              children: "Upsert, Update, and Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Discounts"
              })
            }), _jsx(_components.td, {
              children: "Update and Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Inventory Item"
              })
            }), _jsx(_components.td, {
              children: "Update only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Inventory Level"
              }), " (REST API only)"]
            }), _jsx(_components.td, {
              children: "Upsert, Update, and Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Products"
              })
            }), _jsx(_components.td, {
              children: "Upsert, Update, and Insert"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Check out the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes docs"
        }), " to learn more."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Based on the Shopify object, database records can be matched from your source to your Shopify workspace by a unique property."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Supported Identifiers"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customers"
              })
            }), _jsx(_components.td, {
              children: "Email, ID, or phone"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Discounts"
              })
            }), _jsx(_components.td, {
              children: "ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Inventory Item"
              })
            }), _jsx(_components.td, {
              children: "Inventory item ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Inventory Level"
              })
            }), _jsx(_components.td, {
              children: "Inventory item ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Products"
              })
            }), _jsx(_components.td, {
              children: "Handle"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Shopify's default and custom fields."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You must provide a ", _jsx(_components.strong, {
            children: "Price Rule ID"
          }), " from an existing ", _jsx(_components.strong, {
            children: "PriceRule"
          }), " when\nsyncing ", _jsx(_components.strong, {
            children: "Discounts"
          }), ". Similarly, you must provide an ", _jsx(_components.strong, {
            children: "Inventory Item ID"
          }), "\nfrom an existing ", _jsx(_components.strong, {
            children: "Product"
          }), " when syncing either ", _jsx(_components.strong, {
            children: "Inventory Item"
          }), " or\n", _jsx(_components.strong, {
            children: "Inventory Level"
          }), "."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Products"
        }), " (for the ", _jsx(_components.strong, {
          children: "REST API"
        }), " protocol only), you can choose what Hightouch's behavior is when a record leaves the query result set."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the product in your shop"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete the Shopify product"
              })
            }), _jsx(_components.td, {
              children: "Remove the product from your shop"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to these Shopify events with the following sync modes:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Fulfillment event"
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          })
        })]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You need to include at least the ", _jsx(_components.strong, {
          children: "Fulfillment ID"
        }), ", ", _jsx(_components.strong, {
          children: "Order ID"
        }), ", and ", _jsx(_components.strong, {
          children: "Status"
        }), " in your sync. Other field mappings are optional."]
      }), "\n", _jsx(_components.h3, {
        id: "actions",
        children: "Actions"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to these Shopify events with the following sync modes:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cancel fulfillment orders"
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Mark fulfillment orders as incomplete"
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsxs(_components.strong, {
                children: ["Apply fulfillment hold on fulfillment orders with status ", _jsx(_components.code, {
                  children: "Open"
                })]
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Move fulfillment orders to another location"
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Mark fulfillment orders as open"
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Release the fulfillment hold on fulfillment orders"
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Reschedule the time of scheduled fulfillment orders"
              })
            }), _jsx(_components.td, {
              children: "Insert only"
            })]
          })]
        })]
      }), "\n", _jsx(EventSyncWarning, {
        type: "action"
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Each action requires you to include at least the ", _jsx(_components.strong, {
          children: "Fulfillment Order ID"
        }), ". Some actions require additional fields:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Additional required fields"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsxs(_components.strong, {
                children: ["Apply fulfillment hold on fulfillment orders with status ", _jsx(_components.code, {
                  children: "Open"
                })]
              })
            }), _jsx(_components.td, {
              children: "Reason"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Move fulfillment orders to another location"
              })
            }), _jsx(_components.td, {
              children: "New Location ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Reschedule the time of scheduled fulfillment orders"
              })
            }), _jsx(_components.td, {
              children: "New Fulfill At"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to destinations, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error\ncodes"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After you configure your sync, run a test to confirm data flows correctly:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Start with a small test audience or a single record."
        }), "\n", _jsxs(_components.li, {
          children: ["Trigger a manual sync from the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: "Syncs overview"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["After the sync completes, verify the data in Shopify:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["For customer syncs, go to ", _jsx(_components.strong, {
                children: "Customers"
              }), " in Shopify Admin and search for the test customer. Confirm that the mapped fields appear with the expected values."]
            }), "\n", _jsxs(_components.li, {
              children: ["For product syncs, go to ", _jsx(_components.strong, {
                children: "Products"
              }), " and verify the synced product attributes."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsx(_components.li, {
          children: "If the sync shows errors, check the sync run log in Hightouch for specific API error messages."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "tag-sync-behavior",
        children: "Tag sync behavior"
      }), "\n", _jsx(_components.p, {
        children: "When syncing tags to Shopify, Hightouch replaces the entire tag list on the resource with the tags in your sync payload. This means that if you sync a single tag, any existing tags not included in the payload are removed. To preserve existing tags, include all desired tags in your model's tag column — not just the new ones you want to add."
      }), "\n", _jsx(_components.h3, {
        id: "variants-field-formatting",
        children: "Variants field formatting"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing product variants, the ", _jsx(_components.strong, {
          children: "Variants"
        }), " field expects an array of objects. In your model, ensure the column is cast to the ", _jsx(_components.strong, {
          children: "Object/Array"
        }), " data type — not ", _jsx(_components.strong, {
          children: "String"
        }), ". Each object in the array represents a single variant."]
      }), "\n", _jsx(_components.p, {
        children: "For example, the column value should look like:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"option1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Red\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"option2\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Small\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"19.99\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sku\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"RED-SM\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"option1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Blue\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"option2\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Large\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"price\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"24.99\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sku\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"BLU-LG\""
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If the column is a string, cast it to ", _jsx(_components.strong, {
          children: "Object/Array"
        }), " in your ", _jsx(_components.a, {
          href: "/models/data-types-casting#casting-from-your-model-configuration",
          children: "model configuration"
        }), ", or use your warehouse's JSON functions to construct the array in your SQL model."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
